// Pure pick-lifecycle transition for the admission desk (pick-lifecycle spec
// §1-2). No React. One pick ref in the component; only a LIVE pick
// (loading | awaitingRow) may move the pane.
import { bucketPartitions, isDischargeWindowView } from './deskFilters';

export const PICK_PHASE = {
  IDLE: 'idle',
  LOADING: 'loading',
  AWAITING_ROW: 'awaitingRow',
  OPENED: 'opened',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

export const PICK_IDLE = {
  token: 0,
  id: null,
  source: null,
  phase: PICK_PHASE.IDLE,
  needsReload: false,
  settleSeq: 0,
};

// The primitive the load effect keys on (single source of truth).
export function loadKeyOf(f) {
  return isDischargeWindowView(f)
    ? `D:${f.dfrom}|${f.dto}`
    : bucketPartitions(f).join(',');
}

const settleSeqOf = (needsReload, inFlight, loadSeq) =>
  needsReload || !inFlight ? loadSeq + 1 : loadSeq;

const unchanged = (pick) => ({ pick });

// HIGHLIGHT rules T17-T19. ev: { id, inRows, inFlight, needsReload, loadSeq }
function highlight(pick, ev) {
  const token = pick.token + 1;
  const base = { token, id: ev.id, source: 'highlight' };
  if (ev.needsReload || ev.inFlight) {
    return {
      pick: {
        ...base,
        phase: PICK_PHASE.AWAITING_ROW,
        needsReload: !!ev.needsReload,
        settleSeq: settleSeqOf(ev.needsReload, ev.inFlight, ev.loadSeq),
      },
    };
  }
  const phase = ev.inRows ? PICK_PHASE.OPENED : PICK_PHASE.FAILED;
  return {
    pick: { ...base, phase, needsReload: false, settleSeq: 0 },
    ...(ev.inRows ? { open: { id: ev.id, row: null } } : {}),
  };
}

/**
 * pickTransition(pick, event) → { pick, open?, pickedRow?, notice? }
 * Events:
 *   PICK        { id, inRows, needsReload, inFlight, loadSeq }
 *   GET_OK      { token, row }
 *   GET_FAIL    { token }
 *   ROWS_LOADED { seq, ids:Set, ok }
 *   CANCEL
 *   HIGHLIGHT   { id, inRows, inFlight, needsReload, loadSeq }
 *   PROBE_IN_SCOPE     { startToken, ...HIGHLIGHT fields }
 *   PROBE_OUT_OF_SCOPE { startToken }  → { outOfScope:true } when not stale
 */
export function pickTransition(pick, ev) {
  const live =
    pick.phase === PICK_PHASE.LOADING || pick.phase === PICK_PHASE.AWAITING_ROW;
  switch (ev.type) {
    case 'PICK': {
      const token = pick.token + 1;
      const base = { token, id: ev.id, source: 'search' };
      if (ev.inRows) {
        // T1
        return {
          pick: { ...base, phase: PICK_PHASE.OPENED, needsReload: false, settleSeq: 0 },
          open: { id: ev.id, row: null },
        };
      }
      // T2
      return {
        pick: {
          ...base,
          phase: PICK_PHASE.LOADING,
          needsReload: !!ev.needsReload,
          settleSeq: settleSeqOf(ev.needsReload, ev.inFlight, ev.loadSeq),
        },
      };
    }
    case 'GET_OK': {
      if (ev.token !== pick.token) return unchanged(pick); // T7
      if (pick.phase === PICK_PHASE.LOADING) {
        // T3
        return {
          pick: { ...pick, phase: PICK_PHASE.OPENED, needsReload: false },
          open: { id: pick.id, row: ev.row },
          pickedRow: ev.row,
        };
      }
      // T4 (same token, opened) — pickedRow only; T8 otherwise
      if (pick.phase === PICK_PHASE.OPENED) return { pick, pickedRow: ev.row };
      return unchanged(pick);
    }
    case 'GET_FAIL': {
      if (ev.token !== pick.token || pick.phase !== PICK_PHASE.LOADING) {
        return unchanged(pick); // T7
      }
      if (pick.needsReload) {
        // T5
        return { pick: { ...pick, phase: PICK_PHASE.AWAITING_ROW } };
      }
      // T6
      return { pick: { ...pick, phase: PICK_PHASE.FAILED }, notice: 'failed' };
    }
    case 'ROWS_LOADED': {
      if (!live) return unchanged(pick); // T15
      const found = ev.ok && ev.ids.has(pick.id);
      if (found) {
        // T9 / T12
        return {
          pick: { ...pick, phase: PICK_PHASE.OPENED, needsReload: false },
          open: { id: pick.id, row: null },
        };
      }
      if (ev.seq < pick.settleSeq) return unchanged(pick); // T11 / T14
      if (pick.phase === PICK_PHASE.LOADING) {
        // T10
        return { pick: { ...pick, needsReload: false } };
      }
      // T13
      return { pick: { ...pick, phase: PICK_PHASE.FAILED }, notice: 'failed' };
    }
    case 'CANCEL': // T16
      return {
        pick: {
          ...pick,
          token: pick.token + 1,
          phase: PICK_PHASE.CANCELLED,
          needsReload: false,
        },
      };
    case 'HIGHLIGHT':
      return highlight(pick, ev);
    case 'PROBE_IN_SCOPE':
      return ev.startToken === pick.token ? highlight(pick, ev) : unchanged(pick); // T20/T21
    case 'PROBE_OUT_OF_SCOPE':
      return ev.startToken === pick.token
        ? { pick, outOfScope: true }
        : unchanged(pick);
    default:
      return unchanged(pick);
  }
}
